import { useCallback, useRef, useEffect, useState } from "react";

// Simple sound generation using Web Audio API
const createAudioContext = () => {
  return new (window.AudioContext || window.webkitAudioContext)();
};

export function useSoundEffects(enabled = true) {
  const audioContextRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize audio context
  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = createAudioContext();
      // Resume if suspended (browsers require user gesture)
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
      setIsInitialized(true);
    }
  }, []);

  useEffect(() => {
    // Try to initialize immediately
    initAudio();
    
    // Also listen for any user interaction to resume suspended context
    const handleInteraction = () => {
      if (audioContextRef.current?.state === 'suspended') {
        audioContextRef.current.resume();
      }
      if (!audioContextRef.current) {
        initAudio();
      }
    };
    
    window.addEventListener('click', handleInteraction);
    window.addEventListener('touchstart', handleInteraction);
    window.addEventListener('keydown', handleInteraction);
    
    return () => {
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
    };
  }, [initAudio]);

  const playTone = useCallback((frequency, duration, type = 'sine', volume = 0.3) => {
    if (!enabled) return;
    
    // Try to initialize if not ready
    if (!audioContextRef.current) {
      audioContextRef.current = createAudioContext();
    }
    
    // Resume if suspended
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }

    const ctx = audioContextRef.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = type;
    gainNode.gain.setValueAtTime(volume, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration);
  }, [enabled]);

  // Play noise burst (for explosions)
  const playNoise = useCallback((duration = 0.15, volume = 0.2) => {
    if (!enabled) return;
    
    // Try to initialize if not ready
    if (!audioContextRef.current) {
      audioContextRef.current = createAudioContext();
    }
    
    // Resume if suspended
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }

    const ctx = audioContextRef.current;
    const bufferSize = ctx.sampleRate * duration;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);

    // Generate white noise
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 2);
    }

    const noise = ctx.createBufferSource();
    const gainNode = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    noise.buffer = buffer;
    filter.type = 'lowpass';
    filter.frequency.value = 1000;

    noise.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(ctx.destination);

    gainNode.gain.setValueAtTime(volume, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);

    noise.start(ctx.currentTime);
    noise.stop(ctx.currentTime + duration);
  }, [enabled]);

  // Play sweep sound (for line clears)
  const playSweep = useCallback((direction = 'up', duration = 0.3, volume = 0.25) => {
    if (!enabled) return;
    
    // Try to initialize if not ready
    if (!audioContextRef.current) {
      audioContextRef.current = createAudioContext();
    }
    
    // Resume if suspended
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }

    const ctx = audioContextRef.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.type = 'sine';
    
    if (direction === 'up') {
      oscillator.frequency.setValueAtTime(200, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + duration);
    } else {
      oscillator.frequency.setValueAtTime(800, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + duration);
    }

    gainNode.gain.setValueAtTime(volume, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration);
  }, [enabled]);

  const playSound = useCallback((soundType) => {
    if (!enabled) return;

    switch (soundType) {
      case 'select':
        playTone(440, 0.1, 'sine', 0.2);
        break;
      case 'match':
        // Cheerful ascending notes
        playTone(523, 0.15, 'sine', 0.3);
        setTimeout(() => playTone(659, 0.15, 'sine', 0.3), 100);
        setTimeout(() => playTone(784, 0.2, 'sine', 0.3), 200);
        break;
      case 'swap':
        playTone(330, 0.1, 'triangle', 0.2);
        break;
      case 'invalid':
        playTone(200, 0.2, 'sawtooth', 0.15);
        break;
      case 'explosion':
        // Juicy fruit peel/squish sound for fruit elimination
        // Rising tone simulates peeling, soft finish
        playTone(400, 0.08, 'sine', 0.2);
        setTimeout(() => playTone(600, 0.1, 'sine', 0.25), 40);
        setTimeout(() => playTone(800, 0.06, 'sine', 0.15), 100);
        break;
      case 'cascade':
        // Multiple juicy squishes for cascade
        playTone(500, 0.06, 'sine', 0.18);
        setTimeout(() => playTone(700, 0.08, 'sine', 0.2), 60);
        setTimeout(() => playTone(550, 0.05, 'sine', 0.12), 120);
        break;
      case 'lineClear':
        // Sweep sound for horizontal/vertical line clear
        playSweep('up', 0.25, 0.3);
        setTimeout(() => playTone(880, 0.15, 'sine', 0.2), 100);
        break;
      case 'columnClear':
        // Different sweep for column
        playSweep('down', 0.25, 0.3);
        setTimeout(() => playTone(660, 0.15, 'sine', 0.2), 100);
        break;
      case 'discoClear':
        // Magical sweep for disco melon
        playSweep('up', 0.4, 0.3);
        setTimeout(() => playSweep('down', 0.3, 0.2), 200);
        setTimeout(() => playTone(1047, 0.2, 'sine', 0.25), 400);
        break;
      case 'swordSlash':
        // Whoosh sound for sword
        playSweep('up', 0.15, 0.35);
        setTimeout(() => playSweep('down', 0.15, 0.3), 80);
        playNoise(0.1, 0.2);
        break;
      case 'levelUp':
        // Celebratory level up fanfare
        const levelUpNotes = [523, 659, 784, 880, 1047];
        levelUpNotes.forEach((freq, i) => {
          setTimeout(() => playTone(freq, 0.25, 'sine', 0.35), i * 120);
        });
        // Add sparkle effect
        setTimeout(() => playTone(1319, 0.3, 'sine', 0.2), 600);
        setTimeout(() => playTone(1568, 0.4, 'sine', 0.15), 750);
        break;
      case 'win':
        // Victory fanfare
        const winNotes = [523, 659, 784, 1047];
        winNotes.forEach((freq, i) => {
          setTimeout(() => playTone(freq, 0.3, 'sine', 0.3), i * 150);
        });
        break;
      case 'lose':
        // Sad descending notes
        playTone(392, 0.3, 'sine', 0.25);
        setTimeout(() => playTone(330, 0.3, 'sine', 0.25), 200);
        setTimeout(() => playTone(262, 0.5, 'sine', 0.25), 400);
        break;
      default:
        break;
    }
  }, [enabled, playTone, playNoise, playSweep]);

  return { playSound };
}

// Background music hook
export function useBackgroundMusic() {
  const audioContextRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.15);
  const gainNodeRef = useRef(null);
  const oscillatorsRef = useRef([]);
  const intervalRef = useRef(null);

  // Initialize audio context
  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      gainNodeRef.current = audioContextRef.current.createGain();
      gainNodeRef.current.connect(audioContextRef.current.destination);
      gainNodeRef.current.gain.value = volume;
    }
    return audioContextRef.current;
  }, [volume]);

  // Fun fruity melody - cheerful and bouncy
  const melodyNotes = [
    { freq: 523, dur: 0.2 },  // C5
    { freq: 587, dur: 0.2 },  // D5
    { freq: 659, dur: 0.2 },  // E5
    { freq: 523, dur: 0.2 },  // C5
    { freq: 698, dur: 0.3 },  // F5
    { freq: 659, dur: 0.2 },  // E5
    { freq: 587, dur: 0.2 },  // D5
    { freq: 523, dur: 0.4 },  // C5
    { freq: 784, dur: 0.2 },  // G5
    { freq: 698, dur: 0.2 },  // F5
    { freq: 659, dur: 0.2 },  // E5
    { freq: 587, dur: 0.2 },  // D5
    { freq: 523, dur: 0.3 },  // C5
    { freq: 392, dur: 0.2 },  // G4
    { freq: 440, dur: 0.2 },  // A4
    { freq: 523, dur: 0.5 },  // C5
  ];

  const bassNotes = [
    { freq: 131, dur: 0.4 },  // C3
    { freq: 131, dur: 0.4 },  // C3
    { freq: 175, dur: 0.4 },  // F3
    { freq: 175, dur: 0.4 },  // F3
    { freq: 196, dur: 0.4 },  // G3
    { freq: 196, dur: 0.4 },  // G3
    { freq: 131, dur: 0.4 },  // C3
    { freq: 131, dur: 0.4 },  // C3
  ];

  const playNote = useCallback((freq, duration, type = 'sine', vol = 0.1) => {
    const ctx = audioContextRef.current;
    if (!ctx || !gainNodeRef.current) return;

    const osc = ctx.createOscillator();
    const noteGain = ctx.createGain();

    osc.connect(noteGain);
    noteGain.connect(gainNodeRef.current);

    osc.type = type;
    osc.frequency.value = freq;

    noteGain.gain.setValueAtTime(vol, ctx.currentTime);
    noteGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + duration + 0.1);

    return osc;
  }, []);

  const startMusic = useCallback(() => {
    const ctx = initAudio();
    if (!ctx) return;

    // Resume context if suspended
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    setIsPlaying(true);

    let melodyIndex = 0;
    let bassIndex = 0;
    let beatCount = 0;

    const playBeat = () => {
      if (!audioContextRef.current) return;

      // Play melody note
      const melodyNote = melodyNotes[melodyIndex % melodyNotes.length];
      playNote(melodyNote.freq, melodyNote.dur, 'sine', 0.08);
      melodyIndex++;

      // Play bass every 2 beats
      if (beatCount % 2 === 0) {
        const bassNote = bassNotes[bassIndex % bassNotes.length];
        playNote(bassNote.freq, bassNote.dur, 'triangle', 0.06);
        bassIndex++;
      }

      // Add occasional percussion (high pitched tick)
      if (beatCount % 4 === 0) {
        playNote(1200, 0.05, 'square', 0.03);
      }

      beatCount++;
    };

    // Play at ~140 BPM (roughly 430ms per beat)
    intervalRef.current = setInterval(playBeat, 430);
    playBeat(); // Start immediately
  }, [initAudio, playNote]);

  const stopMusic = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  const toggleMusic = useCallback(() => {
    if (isPlaying) {
      stopMusic();
    } else {
      startMusic();
    }
  }, [isPlaying, startMusic, stopMusic]);

  const setMusicVolume = useCallback((newVolume) => {
    setVolume(newVolume);
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = newVolume;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopMusic();
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [stopMusic]);

  return { 
    isPlaying, 
    startMusic, 
    stopMusic, 
    toggleMusic, 
    volume, 
    setMusicVolume 
  };
}
